import React, { useState, useEffect, useRef } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatPhoneNumber } from "app/routes/Contacts/utils";
import PrivateSessions from "./PrivateSessions";
import PackagesSubscriptions from "./PackagesSubscriptions";
import EventsClinics from "./EventsClinics";
import API, { Profile, Session, Package, Event } from "../services/api";

import phoneIcon from "../assets/phone.svg";
import mailIcon from "../assets/mail.svg";
import linkIcon from "../assets/link.svg";
import locationIcon from "../assets/location.svg";
import leftIcon from "../assets/left.svg";
import rightIcon from "../assets/right.svg";

interface MainContentProps {
  username: string | null | undefined;
  coachExternalId?: string | null | undefined;
  profile?: Profile | null;
  sessions?: Session[];
  packages?: Package[];
  events?: Event[];
  publicCalendarUrl?: string;
  publicProgramsUrl?: string;
  /**
   * Where the tabs point. Defaults to the public profile's own URL; the in-app
   * coach page passes its route so the same four tabs work inside the app shell.
   */
  basePath?: string;
  /** In-app: book a session without leaving for the public calendar. */
  onBookSession?: (session: Session) => void;
  /** In-app: buy a package as a signed-in athlete, skipping the sign-in bridge. */
  onPurchasePackage?: (pkg: Package) => void;
  /**
   * Rendered between About and the tabs. The signed-in coach page puts the Spaces
   * and Groups the athlete shares with the coach there (frame 43408:57223); a
   * public visitor shares nothing, so the public page passes none.
   */
  aboveTabs?: React.ReactNode;
  /**
   * The heading over the packages on the Overview tab. The signed-in frame calls
   * them "Offerings"; the tab keeps its own name either way.
   */
  packagesHeading?: string;
}

const MainContent: React.FC<MainContentProps> = ({
  username,
  coachExternalId,
  profile: propProfile,
  sessions: propSessions,
  packages: propPackages,
  events: propEvents,
  publicCalendarUrl,
  publicProgramsUrl,
  basePath,
  onBookSession,
  onPurchasePackage,
  aboveTabs,
  packagesHeading,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { facilitySlug, coachExternalId: urlCoachExternalId } = useParams<{
    facilitySlug: string;
    coachExternalId: string;
  }>();
  const currentCoachExternalId = coachExternalId || urlCoachExternalId;
  const locationsContainerRef = useRef<HTMLDivElement>(null);
  const aboutTextRef = useRef<HTMLParagraphElement>(null);
  const scrollListenerAttached = useRef(false);

  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  /**
   * The maps a visitor has taken hold of.
   *
   * The tile is inert until it is clicked. A live Google map that is only 140
   * tall swallows the wheel and, on a phone, the drag that was meant to scroll
   * the page past it — so it stays a picture until somebody asks for the map,
   * and is a map from then on.
   */
  const [activeMaps, setActiveMaps] = useState<Set<number>>(new Set());

  const activateMap = (id: number) =>
    setActiveMaps((current) => new Set(current).add(id));
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [currentLocationIndex, setCurrentLocationIndex] = useState(0);
  const [showReadMore, setShowReadMore] = useState(false);

  const tabBasePath =
    basePath || `/facility/${facilitySlug}/coach/${username}/${currentCoachExternalId}`;

  // Read from the end of the path rather than matching the whole prefix, so the
  // same four tabs work under either base path.
  const getActiveTab = (): string => {
    const path = location.pathname.replace(/\/$/, "");
    if (path.endsWith("/private")) {
      return "private-sessions";
    }
    if (path.endsWith("/packages")) {
      return "packages";
    }
    if (path.endsWith("/events")) {
      return "events";
    }
    return "overview";
  };

  const handleLocationScroll = () => {
    if (locationsContainerRef.current) {
      const container = locationsContainerRef.current;
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.children[0]?.clientWidth || 0;
      const gap = 12;
      const newIndex = Math.round(scrollLeft / (cardWidth + gap));

      if (newIndex !== currentLocationIndex) {
        setCurrentLocationIndex(newIndex);
      }
    }
  };

  const attachScrollListener = () => {
    if (locationsContainerRef.current && !scrollListenerAttached.current) {
      locationsContainerRef.current.addEventListener(
        "scroll",
        handleLocationScroll,
      );
      scrollListenerAttached.current = true;
    }
  };

  const detachScrollListener = () => {
    if (locationsContainerRef.current) {
      locationsContainerRef.current.removeEventListener(
        "scroll",
        handleLocationScroll,
      );
      scrollListenerAttached.current = false;
    }
  };

  const loadAllData = async () => {
    setInitialLoading(true);
    setProfileLoading(true);

    if (!username) {
      return;
    }

    try {
      const [profileRes, sessionsRes, eventsRes] = await Promise.all([
        API.getCoachProfile(username),
        API.getPrivateSessions(username),
        API.getEvents(username),
      ]);

      setProfile(profileRes.data.profile);
      setSessions(sessionsRes.data.sessions);
      setEvents(eventsRes.data.events);
      setProfileLoading(false);
      setInitialLoading(false);
    } catch (error) {
      console.error("Error loading data:", error);
      setProfileLoading(false);
      setInitialLoading(false);
    }
  };

  const checkTextOverflow = () => {
    if (aboutTextRef.current) {
      const element = aboutTextRef.current;
      const isOverflowing = element.scrollHeight > element.clientHeight;
      setShowReadMore(isOverflowing);
    }
  };

  const toggleAboutExpanded = () => {
    setIsAboutExpanded(!isAboutExpanded);
  };

  const nextLocation = () => {
    if (
      profile &&
      profile.locations &&
      currentLocationIndex < profile.locations.length - 1
    ) {
      const newIndex = currentLocationIndex + 1;
      setCurrentLocationIndex(newIndex);
      scrollToLocation(newIndex);
    }
  };

  const prevLocation = () => {
    if (currentLocationIndex > 0) {
      const newIndex = currentLocationIndex - 1;
      setCurrentLocationIndex(newIndex);
      scrollToLocation(newIndex);
    }
  };

  const scrollToLocation = (index: number) => {
    if (locationsContainerRef.current) {
      const container = locationsContainerRef.current;
      const locationCards = container.children;
      if (locationCards[index]) {
        locationCards[index].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      }
    }
  };

  useEffect(() => {
    if (propProfile && propSessions && propPackages && propEvents) {
      setProfile(propProfile);
      setSessions(propSessions);
      setPackages(propPackages);
      setEvents(propEvents);
      setProfileLoading(false);
      setInitialLoading(false);
      setTimeout(() => {
        attachScrollListener();
      }, 100);
    } else {
      loadAllData();
      attachScrollListener();
    }

    return () => {
      detachScrollListener();
    };
  }, [username]);

  // Update internal state when props change
  useEffect(() => {
    if (propProfile) {
      setProfile(propProfile);
    }
  }, [propProfile]);

  useEffect(() => {
    if (propSessions) {
      setSessions(propSessions);
    }
  }, [propSessions]);

  useEffect(() => {
    if (propPackages) {
      setPackages(propPackages);
    }
  }, [propPackages]);

  useEffect(() => {
    if (propEvents) {
      setEvents(propEvents);
    }
  }, [propEvents]);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    if (profile) {
      setTimeout(() => {
        checkTextOverflow();
      }, 0);
    }
  }, [profile]);

  useEffect(() => {
    if (!initialLoading) {
      setTimeout(() => {
        attachScrollListener();
        checkTextOverflow();
      }, 100);
    }
  }, [initialLoading]);

  const renderSkeleton = () => {
    const activeTab = getActiveTab();
    const showHeading = activeTab === "overview";

    const sessionSkeleton = (
      <div className="pub-section">
        {showHeading && (
          <div className="pub-section-header">
            <div className="pub-skeleton pub-skeleton-heading"></div>
          </div>
        )}
        <div className="pub-sessions-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="pub-session-card">
              <div className="pub-session-header">
                <div className="pub-skeleton pub-skeleton-duration"></div>
                <div className="pub-session-title-wrapper">
                  <div className="pub-skeleton pub-skeleton-title"></div>
                  <div className="pub-skeleton pub-skeleton-description-line"></div>
                </div>
              </div>
              <div className="pub-session-footer">
                <div className="pub-skeleton pub-skeleton-price"></div>
                <div className="pub-skeleton pub-skeleton-button"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );

    const packageSkeleton = (
      <div className="pub-section">
        {showHeading && (
          <div className="pub-section-header">
            <div className="pub-skeleton pub-skeleton-heading"></div>
          </div>
        )}
        <div className="pub-packages-list">
          {[1, 2, 3].map((i) => (
            <div key={i} className="pub-package-card">
              <div className="pub-package-header-row">
                <div className="pub-skeleton pub-skeleton-title"></div>
                <div className="pub-skeleton pub-skeleton-sessions"></div>
              </div>
              <div className="pub-skeleton pub-skeleton-package-desc"></div>
              <div className="pub-package-footer">
                <div className="pub-skeleton pub-skeleton-price"></div>
                <div className="pub-skeleton pub-skeleton-button"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );

    const eventSkeleton = (
      <div className="pub-section">
        {showHeading && (
          <div className="pub-section-header">
            <div className="pub-skeleton pub-skeleton-heading"></div>
          </div>
        )}
        <div className="pub-events-grid">
          {[1].map((i) => (
            <div key={i} className="pub-event-card">
              <div className="pub-skeleton pub-skeleton-event-image"></div>
              <div className="pub-event-card-right">
                <div className="pub-event-content">
                  <div className="pub-skeleton pub-skeleton-event-title"></div>
                  <div className="pub-skeleton pub-skeleton-description-line"></div>
                  <div className="pub-skeleton pub-skeleton-description-line"></div>
                </div>
                <div className="pub-event-footer">
                  <div className="pub-skeleton pub-skeleton-price"></div>
                  <div className="pub-event-actions">
                    <div className="pub-skeleton pub-skeleton-button"></div>
                    <div className="pub-skeleton pub-skeleton-button"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );

    if (activeTab === "overview") {
      return (
        <>
          {sessionSkeleton}
          {packageSkeleton}
          {eventSkeleton}
        </>
      );
    } else if (activeTab === "private-sessions") {
      return sessionSkeleton;
    } else if (activeTab === "packages") {
      return packageSkeleton;
    } else if (activeTab === "events") {
      return eventSkeleton;
    }
    // Default return for unknown tabs
    return null;
  };

  const showMoreComponent = (
    <>
      {isAboutExpanded && (
        <>
          <h2 className="pub-awards-title">{t("Awards")}</h2>
          <p className="pub-about-text">{profile?.awards || ""}</p>
        </>
      )}
      <button className="pub-view-all-link" onClick={toggleAboutExpanded}>
        {isAboutExpanded ? t("Show Less") : t("Show More")}
      </button>
    </>
  );

  const activeTab = getActiveTab();

  return (
    <div className="pub-main-content">
      <div className="pub-content-wrapper">
        <div className="pub-about-section">
          {profileLoading ? (
            <>
              <div className="pub-skeleton pub-skeleton-about-title"></div>
              <div className="pub-skeleton pub-skeleton-description"></div>
            </>
          ) : (
            // A coach who wrote no bio gets no heading either: "About" over
            // nothing reads as something that failed to load.
            profile &&
            !!profile.about?.trim() && (
              <>
                <h2 className="pub-about-title">{t("About")}</h2>
                <div className="pub-about-text-wrapper">
                  <p
                    ref={aboutTextRef}
                    className={`pub-about-text ${!isAboutExpanded ? "pub-about-text-collapsed" : ""}`}
                  >
                    {profile.about || ""}
                  </p>
                  {(showReadMore || isAboutExpanded) && showMoreComponent}
                </div>
              </>
            )
          )}
        </div>

        {/* How to reach the coach, and where they work — side by side under the
            bio (43593:29116). Both blocks were built for the narrow screen and
            hidden above it, because the sidebar carried the desktop copies;
            with the sidebar gone they are the only copies at every width. */}
        <div className="pub-details-row">
        {(profileLoading ||
          (profile &&
            (profile.contact.phone ||
              profile.contact.email ||
              profile.contact.website))) && (
          <div className="pub-contact-section pub-contact-section-mobile">
            {profileLoading ? (
              <>
                <div className="pub-skeleton pub-skeleton-section-title"></div>
                <div className="pub-contact-item">
                  <div className="pub-skeleton pub-skeleton-contact-icon"></div>
                  <div className="pub-skeleton pub-skeleton-contact-text"></div>
                </div>
                <div className="pub-contact-item">
                  <div className="pub-skeleton pub-skeleton-contact-icon"></div>
                  <div className="pub-skeleton pub-skeleton-contact-text"></div>
                </div>
                <div className="pub-contact-item">
                  <div className="pub-skeleton pub-skeleton-contact-icon"></div>
                  <div className="pub-skeleton pub-skeleton-contact-text"></div>
                </div>
              </>
            ) : (
              profile && (
                <>
                  <h3 className="pub-section-title">
                    {t("Contact Information")}
                  </h3>
                  {profile.contact.phone && (
                    <div className="pub-contact-item">
                      <div className="pub-contact-icon-wrapper">
                        <img
                          src={phoneIcon}
                          alt="Phone"
                          className="pub-contact-icon"
                        />
                      </div>
                      {/* The sidebar these rows replaced dialled and composed;
                          plain text made a visitor copy the number out by hand. */}
                      <a
                        href={`tel:${profile.contact.phone}`}
                        className="pub-contact-link"
                      >
                        {formatPhoneNumber(profile.contact.phone)}
                      </a>
                    </div>
                  )}
                  {profile.contact.email && (
                    <div className="pub-contact-item">
                      <div className="pub-contact-icon-wrapper">
                        <img
                          src={mailIcon}
                          alt="Email"
                          className="pub-contact-icon"
                        />
                      </div>
                      <a
                        href={`mailto:${profile.contact.email}`}
                        className="pub-contact-link"
                      >
                        {profile.contact.email}
                      </a>
                    </div>
                  )}
                  {profile.contact.website && (
                    <div className="pub-contact-item">
                      <div className="pub-contact-icon-wrapper pub-contact-icon-wrapper-link">
                        <img
                          src={linkIcon}
                          alt="Website"
                          className="pub-contact-icon"
                        />
                      </div>
                      <a
                        href={
                          profile.contact.website?.match(/^https?:\/\//)
                            ? profile.contact.website
                            : `https://${profile.contact.website}`
                        }
                        className="pub-contact-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {profile.contact.website}
                      </a>
                    </div>
                  )}
                </>
              )
            )}
          </div>
        )}

        {/* Where the coach works, beside their contacts. */}
        {!initialLoading &&
          profile &&
          profile.locations &&
          profile.locations.length > 0 && (
            <div
              className={`pub-locations-section pub-locations-section-mobile${
                profile.locations.length === 1 ? " pub-locations-single" : ""
              }`}
            >
              {/* The frame writes a single place as one line — pin, name,
                  street — with no heading over it (43593:29102). The heading
                  earns its place only when there are several to page through. */}
              {profile.locations.length > 1 && (
                <div className="pub-locations-header">
                  <h3 className="pub-section-title">{t("Locations")}</h3>
                  <div className="pub-locations-nav">
                    <button
                      className="pub-nav-btn"
                      onClick={prevLocation}
                      disabled={currentLocationIndex === 0}
                    >
                      <img
                        src={leftIcon}
                        alt="Previous"
                        className="pub-nav-icon"
                      />
                    </button>
                    <span className="pub-location-counter">
                      {currentLocationIndex + 1}/{profile.locations.length}
                    </span>
                    <button
                      className="pub-nav-btn"
                      onClick={nextLocation}
                      disabled={
                        currentLocationIndex === profile.locations.length - 1
                      }
                    >
                      <img
                        src={rightIcon}
                        alt="Next"
                        className="pub-nav-icon"
                      />
                    </button>
                  </div>
                </div>
              )}
              <div
                className="pub-locations-swipeable"
                ref={locationsContainerRef}
              >
                {profile.locations.map((location) => {
                  /**
                   * The coach payload puts the facility's *name* in the
                   * location's address, and the real one — street, city,
                   * country — arrives separately on the profile. The sidebar
                   * this column replaced used that real one; using the payload
                   * address printed the facility's name twice and gave the map
                   * nothing it could find on earth.
                   */
                  const address = profile.address || location.address;

                  return (
                  <div key={location.id} className="pub-location-block">
                    <div className="pub-location-info">
                      <img
                        src={locationIcon}
                        alt="Location"
                        className="pub-location-icon"
                      />
                      <div className="pub-location-text">
                        <h4 className="pub-location-name">{location.name}</h4>
                        {address && address !== location.name && (
                          <p className="pub-location-address">{address}</p>
                        )}
                      </div>
                    </div>

                    {/*
                      The map the frame draws under the address (43593:29107).
                      It used to live in the sidebar, which this column replaced,
                      so the address was left standing on its own.

                      `?q=<address>&output=embed` needs no API key — the app has
                      none, only a Google OAuth client id — and no lat/lon, which
                      the coach payload does not carry either. It has to be asked
                      of `maps.google.com`: the same query on `www.google.com`
                      refuses to be framed and drew nothing at all, which is why
                      the tile was blank wherever the address was good.

                      Clicking it hands the map over — see `activeMaps`.
                    */}
                    {address && address !== location.name && (
                      <div
                        className={`pub-location-map${
                          activeMaps.has(location.id)
                            ? " pub-location-map-active"
                            : ""
                        }`}
                      >
                        <iframe
                          title={t("Map of {{location}}", {
                            location: location.name || address,
                          })}
                          src={`https://maps.google.com/maps?q=${encodeURIComponent(
                            address,
                          )}&z=15&output=embed`}
                          width="100%"
                          height="140"
                          // This React version's iframe typings predate
                          // `loading`, so it arrives as an attribute.
                          {...{ loading: "lazy" }}
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                        {!activeMaps.has(location.id) && (
                          <button
                            type="button"
                            className="pub-location-map-activate"
                            aria-label={t("Use the map")}
                            onClick={() => activateMap(location.id)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Frame 43408:57223 puts the shared Spaces and Groups here, between the
            profile details and the tabs. Nothing on a public page. */}
        {!initialLoading && aboveTabs}

        <div>
          {initialLoading ? (
            <div className="pub-tabs-skeleton">
              <div className="pub-skeleton pub-skeleton-tab"></div>
              <div className="pub-skeleton pub-skeleton-tab"></div>
              <div className="pub-skeleton pub-skeleton-tab"></div>
              <div className="pub-skeleton pub-skeleton-tab"></div>
            </div>
          ) : (
            /**
             * CFTB-3473: `replace`, not push.
             *
             * These four are views of one page, but each was a history entry, so
             * after looking at three tabs the back button walked back through them
             * — three presses to leave a screen the visitor arrived at once. The
             * profile is one entry now whichever tab is showing, and back goes
             * where the visitor came from.
             */
            <div className="pub-tabs">
              <Link
                replace
                to={tabBasePath}
                className={`pub-tab ${activeTab === "overview" ? "pub-active" : ""}`}
                data-text={t("Overview")}
              >
                {t("Overview")}
              </Link>
              <Link
                replace
                to={`${tabBasePath}/private`}
                className={`pub-tab ${activeTab === "private-sessions" ? "pub-active" : ""}`}
                data-text={t("Private Sessions")}
              >
                {t("Private Sessions")}
              </Link>
              <Link
                replace
                to={`${tabBasePath}/packages`}
                className={`pub-tab ${activeTab === "packages" ? "pub-active" : ""}`}
                data-text={t("Packages & Subscriptions")}
              >
                {t("Packages & Subscriptions")}
              </Link>
              <Link
                replace
                to={`${tabBasePath}/events`}
                className={`pub-tab ${activeTab === "events" ? "pub-active" : ""}`}
                data-text={t("Events & Clinics")}
              >
                {t("Events & Clinics")}
              </Link>
            </div>
          )}

          <div className="pub-tab-content">
            {isLoading || initialLoading ? (
              renderSkeleton()
            ) : (
              <>
                {activeTab === "overview" && (
                  <>
                    {sessions.length > 0 && (
                      <PrivateSessions
                        showViewAll={true}
                        onViewAll={() =>
                          // The same tab change the bar above makes — see the note
                          // on the tabs: it replaces rather than stacks.
                          history.replace(`${tabBasePath}/private`)
                        }
                        sessions={sessions.slice(0, 3)}
                        publicCalendarUrl={publicCalendarUrl}
                        currency={profile?.currency}
                        onBook={onBookSession}
                      />
                    )}
                    {packages.length > 0 && (
                      <PackagesSubscriptions
                        showViewAll={true}
                        onViewAll={() =>
                          // The same tab change the bar above makes — see the note
                          // on the tabs: it replaces rather than stacks.
                          history.replace(`${tabBasePath}/packages`)
                        }
                        packages={packages.slice(0, 4)}
                        coachName={`${profile?.firstName} ${profile?.lastName}`}
                        coachId={currentCoachExternalId}
                        contactPhone={profile?.contact?.phone}
                        contactEmail={profile?.contact?.email}
                        onPurchase={onPurchasePackage}
                        heading={packagesHeading}
                      />
                    )}
                    {events.length > 0 && (
                      <EventsClinics
                        showViewAll={true}
                        onViewAll={() =>
                          // The same tab change the bar above makes — see the note
                          // on the tabs: it replaces rather than stacks.
                          history.replace(`${tabBasePath}/events`)
                        }
                        events={events.slice(0, 3)}
                        publicProgramsUrl={publicProgramsUrl}
                      />
                    )}

                    {/*
                      CFTB-3478: a coach with nothing to book still says so.

                      Every section here draws only when it has something, so a
                      coach with no sessions, packages or events left the page
                      blank below the tabs — the profile card beside it and then
                      an empty expanse, with nothing to say whether it was still
                      loading or simply empty. The other tabs each carry their own
                      empty state; the Overview had none because it is a
                      composition of them.
                    */}
                    {sessions.length === 0 &&
                      packages.length === 0 &&
                      events.length === 0 && (
                        <div className="pub-empty-state">
                          <h3 className="pub-empty-state-title">
                            {t("No available booking options")}
                          </h3>
                          <p className="pub-empty-state-description">
                            {t(
                              "Check back soon, or contact this coach to arrange a session.",
                            )}
                          </p>
                        </div>
                      )}
                  </>
                )}
                {activeTab === "private-sessions" && (
                  <PrivateSessions
                    showViewAll={false}
                    sessions={sessions}
                    publicCalendarUrl={publicCalendarUrl}
                    currency={profile?.currency}
                    onBook={onBookSession}
                  />
                )}
                {activeTab === "packages" && (
                  <PackagesSubscriptions
                    showViewAll={false}
                    packages={packages}
                    coachName={`${profile?.firstName} ${profile?.lastName}`}
                    coachId={currentCoachExternalId}
                    contactPhone={profile?.contact?.phone}
                    contactEmail={profile?.contact?.email}
                    onPurchase={onPurchasePackage}
                    heading={packagesHeading}
                  />
                )}
                {activeTab === "events" && (
                  <EventsClinics
                    showViewAll={false}
                    events={events}
                    publicProgramsUrl={publicProgramsUrl}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainContent;
